import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  CheckCircle, 
  Circle, 
  ArrowRight, 
  Target,
  UserCircle,
  CheckCircle2,
  Sparkles,
  Users,
  X,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { wellnessService } from "@/services/wellnessService";
import { goalsService } from "@/services/goalsService";
import { usersService } from "@/services/usersService";

interface ChecklistStep {
  id: string;
  title: string;
  description: string;
  actionText: string;
  icon: React.ReactNode;
  completed: boolean;
  onClick: () => void;
  isEssential: boolean;
}

// Helper function to mark page as visited in backend progress
const markPageAsVisited = async (userId: string, pageName: string) => {
  try {
    // Get current visited pages - apiService returns data directly
    const progressData = await usersService.getProgress(userId).catch(() => ({}));
    const visitedPagesData = progressData?.visited_pages || {};
    const currentPages = Array.isArray(visitedPagesData.pages) ? visitedPagesData.pages : [];
    
    // Add page if not already visited
    if (!currentPages.includes(pageName)) {
      await usersService.updateProgress(userId, {
        type: 'visited_pages',
        data: { pages: [...currentPages, pageName] }
      });
    }
  } catch (error) {
    console.error(`Error marking page ${pageName} as visited:`, error);
  }
};

export default function WelcomeChecklistCard() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<ChecklistStep[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (!user?.id) return;

    const loadChecklistStatus = async () => {
      try {
        // Parallel fetch all data from API
        // Note: apiService returns data directly (unwrapped from { success, data })
        const [profileData, statsData, goalsData, preferencesData, progressData] = await Promise.all([
          // Get user profile
          usersService.getUserById(user.id).catch(() => null),
          
          // Check if user has any check-ins
          wellnessService.getStats().catch(() => ({ totalCheckIns: 0 })),
            
          // Check if user has any goals
          goalsService.getGoals().catch(() => []),
          
          // Get user preferences (for dismissed status)
          usersService.getPreferences(user.id).catch(() => ({})),
          
          // Get user progress (for visited pages tracking)
          usersService.getProgress(user.id).catch(() => ({}))
        ]);

        // Check if user has dismissed this checklist (from backend preferences)
        const preferences = preferencesData || {};
        const isDismissedFromBackend = preferences.welcomeChecklist_dismissed === true || preferences.welcomeChecklist_dismissed === 'true';
        if (isDismissedFromBackend) {
          setIsDismissed(true);
          return;
        }

        // DEFINITION OF DONE for each checklist item:
        // 1. Complete Profile: User has name (display_name), bio, AND avatar filled in their profile (essential fields)
        const profile = profileData;
        const hasCompleteProfile = profile?.displayName && 
                                  profile?.bio && 
                                  profile?.avatarUrl;
        
        // 2. First Check-in: User has completed at least one check-in
        const totalCheckIns = statsData?.totalCheckIns || 0;
        const hasCheckIns = totalCheckIns > 0;
        
        // 3. Set Goal: User has created at least one goal
        const goals = Array.isArray(goalsData) ? goalsData : (goalsData?.goals || []);
        const hasGoals = goals.length > 0;
        
        // 4. Explore AI Insights: User has visited the /patterns page (tracked in backend progress)
        const progress = progressData || {};
        const visitedPagesData = progress.visited_pages || {};
        const visitedPages = Array.isArray(visitedPagesData.pages) ? visitedPagesData.pages : [];
        const hasExploredInsights = visitedPages.includes('patterns');
        
        // 5. Connect with Team: User has visited the /team page (tracked in backend progress)
        const hasConnectedWithTeam = visitedPages.includes('team');

        const checklistSteps: ChecklistStep[] = [
          {
            id: 'first-checkin',
            title: 'Complete Your First Check-in',
            description: 'Start tracking your wellbeing and establish your baseline.',
            actionText: 'Start Check-in',
            icon: <CheckCircle2 className="h-4 w-4" />,
            completed: hasCheckIns,
            isEssential: true,
            onClick: () => navigate('/check-in')
          },
          {
            id: 'complete-profile',
            title: 'Complete Your Profile',
            description: 'Add your name, bio, and profile picture.',
            actionText: 'Update Profile',
            icon: <UserCircle className="h-4 w-4" />,
            completed: hasCompleteProfile,
            isEssential: true,
            onClick: () => {
              // Trigger the ProfileSettingsWidget to open
              const settingsButton = document.querySelector('[data-testid="profile-settings-trigger"]') as HTMLButtonElement;
              if (settingsButton) {
                settingsButton.click();
              } else {
                // Fallback: navigate to settings page
                navigate('/settings');
              }
            }
          },
          {
            id: 'set-goal',
            title: 'Set Your First Goal',
            description: 'Define what you want to achieve and track progress.',
            actionText: 'Create Goal',
            icon: <Target className="h-4 w-4" />,
            completed: hasGoals,
            isEssential: true,
            onClick: () => navigate('/profile') // Navigate to My Growth page where goals are managed
          },
          {
            id: 'explore-insights',
            title: 'Explore AI Insights',
            description: 'See how Step analyzes your patterns (available after a few check-ins).',
            actionText: 'View Insights',
            icon: <Sparkles className="h-4 w-4" />,
            completed: hasExploredInsights,
            isEssential: false,
            onClick: async () => {
              navigate('/patterns');
              // Mark as visited in backend progress
              if (user?.id) {
                await markPageAsVisited(user.id, 'patterns');
              }
            }
          }
        ];

        // Add team connection step if user is not solo
        if (user?.persona !== 'independentSolo') {
          checklistSteps.push({
            id: 'connect-team',
            title: 'Connect with Your Team',
            description: 'Explore team features and start collaborating.',
            actionText: 'View Team',
            icon: <Users className="h-4 w-4" />,
            completed: hasConnectedWithTeam,
            isEssential: false,
            onClick: async () => {
              navigate('/team');
              // Mark as visited in backend progress
              if (user?.id) {
                await markPageAsVisited(user.id, 'team');
              }
            }
          });
        }

        setSteps(checklistSteps);

        // Always keep expanded on initial load - user can collapse manually if needed

      } catch (error) {
        console.error('Error loading checklist status:', error);
      }
    };

    loadChecklistStatus();
  }, [user?.id, user?.persona, navigate]);

  // Refresh checklist status when user navigates back to dashboard
  useEffect(() => {
    const handleFocus = () => {
      if (user?.id) {
        // Small delay to allow backend updates to complete
        setTimeout(() => {
          const loadChecklistStatus = async () => {
            try {
              // Use API services - note: apiService returns data directly
              const [profileData, statsData, goalsData] = await Promise.all([
                usersService.getUserById(user.id).catch(() => null),
                wellnessService.getStats().catch(() => ({ totalCheckIns: 0 })),
                goalsService.getGoals().catch(() => [])
              ]);

              const profile = profileData;
              const hasCompleteProfile = profile?.displayName && 
                                        profile?.bio && 
                                        profile?.avatarUrl;
              
              const totalCheckIns = statsData?.totalCheckIns || 0;
              const hasCheckIns = totalCheckIns > 0;
              
              const goals = Array.isArray(goalsData) ? goalsData : (goalsData?.goals || []);
              const hasGoals = goals.length > 0;
              
              // Get progress from backend instead of localStorage
              const progressData = await usersService.getProgress(user.id).catch(() => ({}));
              const visitedPagesData = progressData?.visited_pages || {};
              const visitedPages = Array.isArray(visitedPagesData.pages) ? visitedPagesData.pages : [];
              
              const hasExploredInsights = visitedPages.includes('patterns');
              const hasConnectedWithTeam = visitedPages.includes('team');

              setSteps(prevSteps => prevSteps.map(step => ({
                ...step,
                completed: step.id === 'first-checkin' ? hasCheckIns :
                          step.id === 'complete-profile' ? hasCompleteProfile :
                          step.id === 'set-goal' ? hasGoals :
                          step.id === 'explore-insights' ? hasExploredInsights :
                          step.id === 'connect-team' ? hasConnectedWithTeam :
                          step.completed
              })));
            } catch (error) {
              console.error('Error refreshing checklist status:', error);
            }
          };
          loadChecklistStatus();
        }, 500);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.id]);

  const completedSteps = steps.filter(step => step.completed).length;
  const totalSteps = steps.length;
  const progressPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
  const allEssentialCompleted = steps.filter(step => step.isEssential).every(step => step.completed);

  // Don't show if dismissed or all essential steps completed
  if (isDismissed || (allEssentialCompleted && completedSteps >= 3)) {
    return null;
  }

  const handleDismiss = async () => {
    if (!user?.id) return;
    
    setIsDismissed(true);
    // Save dismissed status to backend preferences
    try {
      await usersService.updatePreferences(user.id, {
        welcomeChecklist_dismissed: true
      });
    } catch (error) {
      console.error('Error saving dismissed status:', error);
    }
  };

  return (
    <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-blue-50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Welcome to Step!
            </CardTitle>
            <CardDescription>
              Complete these steps to get the most out of your experience
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1"
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="p-1"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{completedSteps} of {totalSteps} completed</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          <div className="space-y-3">
            {steps.map((step) => (
              <div 
                key={step.id}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                  step.completed 
                    ? "bg-green-50 border-green-200" 
                    : "bg-white border-gray-200 hover:border-primary/30 hover:bg-primary/5"
                )}
              >
                <div className="flex-shrink-0">
                  {step.completed ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={cn(
                      "font-medium text-sm",
                      step.completed ? "text-green-700" : "text-gray-900"
                    )}>
                      {step.title}
                    </h4>
                    {step.isEssential && !step.completed && (
                      <Badge variant="secondary" className="text-xs">Essential</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {step.description}
                  </p>
                </div>

                {!step.completed && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={step.onClick}
                    className="flex items-center gap-1 text-xs"
                  >
                    {step.actionText}
                    <ArrowRight className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}

            {allEssentialCompleted && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Great job! You've completed the essentials.</span>
                </div>
                <p className="text-xs text-green-600 mt-1">
                  You're all set to start your Step journey. Keep checking in daily to build insights!
                </p>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}